import { LocationMapDialog } from '@/components/controls';
import { spin } from '@/utils';
import {
  Autorenew as AutorenewIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
import { Masonry } from '@mui/lab';
import {
  alpha,
  Box,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { timePeriods } from '../utils';
import { AntiSocialBehaviourInstance } from './AntiSocialBehaviourInstance';
import { useNicheAntiSocialBehaviour } from './useNicheAntiSocialBehaviour';

export function AntiSocialBehaviour({ title, groupCodes, onComplete }) {
  const [days, setDays] = useState(1);
  const {
    query: { data: instances, isFetching, refetch },
  } = useNicheAntiSocialBehaviour(groupCodes, days);
  const [selectedId, setSelectedId] = useState(null);
  const { width, ref } = useResizeDetector();
  const columns = useMemo(() => Math.floor(width / 248) || 1, [width]);
  const selectedInstance = useMemo(
    () =>
      instances.find((instance) => instance.occurrence.number === selectedId),
    [instances, selectedId],
  );

  function handleRefreshClick() {
    refetch();
  }

  function handleCompleteClick() {
    onComplete();
  }

  function handleDaysChange(event, value) {
    setDays(+value);
  }

  const handleShowMapClick = (id) => () => {
    setSelectedId(id);
  };

  function handleMapClose() {
    setSelectedId(null);
  }

  return (
    <Box sx={{ px: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={(theme) => ({
          alignItems: 'center',
          position: 'sticky',
          top: 48,
          bgcolor: alpha(theme.palette.background.paper, 0.8),
          zIndex: 700,
          pl: 1,
          py: 1,
        })}
      >
        <Typography variant="h6">{title}</Typography>
        <Box>
          <Tooltip title="Done" placement="right">
            <IconButton onClick={handleCompleteClick}>
              <DoneIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <ToggleButtonGroup
          value={days.toString()}
          onChange={handleDaysChange}
          color="primary"
          size="small"
          exclusive
        >
          {Object.entries(timePeriods).map(([value, label]) => (
            <ToggleButton key={value} value={value}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Box>
          <Tooltip title="Refresh" placement="bottom">
            <Box component="span">
              <IconButton onClick={handleRefreshClick} disabled={isFetching}>
                <AutorenewIcon
                  fontSize="inherit"
                  sx={
                    isFetching
                      ? { animation: `${spin} 2s linear infinite` }
                      : undefined
                  }
                />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      </Stack>
      <Stack spacing={1} sx={{ pb: 1 }} ref={ref}>
        <Masonry spacing={1} columns={columns}>
          {instances.map((instance) => (
            <AntiSocialBehaviourInstance
              key={instance.occurrence.number}
              instance={instance}
              onShowMapClick={handleShowMapClick(instance.occurrence.number)}
            />
          ))}
        </Masonry>
      </Stack>
      {selectedInstance?.occurrence?.address && (
        <LocationMapDialog
          addresses={[selectedInstance.occurrence.address]}
          title={`Points of interest for ${selectedInstance.occurrence.number}`}
          isOpen={Boolean(selectedInstance)}
          onClose={handleMapClose}
        />
      )}
    </Box>
  );
}
